import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Package, ShoppingCart, User, BarChart3, CheckCircle,
    ChevronRight, ChevronLeft, X, ArrowRight, Plus,
    Sparkles, FileText, Store, DollarSign, Clock,
    Eye, Send, AlertCircle, Zap, Target
} from 'lucide-react';
import { Button } from './ui/button';

const VendorOnboarding = ({ onComplete, vendorName, onNavigate }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Load saved progress
    const getSavedStep = () => {
        const saved = localStorage.getItem('vendor_onboarding_step');
        return saved ? parseInt(saved, 10) : 0;
    };

    const [currentStep, setCurrentStep] = useState(getSavedStep());
    const [isMinimized, setIsMinimized] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    // Save progress when step changes
    useEffect(() => {
        localStorage.setItem('vendor_onboarding_step', currentStep.toString());
    }, [currentStep]);

    const steps = [
        {
            id: 'welcome',
            title: 'Welcome to Your Dashboard',
            shortTitle: 'Welcome',
            icon: Sparkles,
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        Hello <strong>{vendorName || 'Vendor'}</strong>! Your account is now active.
                        Let's set up your store step by step.
                    </p>
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-green-800 dark:text-green-300">Account Verified</p>
                                <p className="text-sm text-green-700 dark:text-green-400">You can now start adding products and receiving orders</p>
                            </div>
                        </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        <strong>This wizard will help you:</strong>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                            <li>Add products to your store</li>
                            <li>Understand how to manage orders</li>
                            <li>Set up your profile</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'products',
            title: 'Add Products to Your Store',
            shortTitle: 'Products',
            icon: Package,
            targetPage: '/vendor/products',
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        You have <strong>two ways</strong> to add products:
                    </p>

                    {/* Option 1: From Catalog */}
                    <button
                        onClick={() => {
                            if (currentStep < steps.length - 1) setCurrentStep(prev => prev + 1);
                            handleMinimize();
                            navigate('/vendor/products', { state: { action: 'browse_catalog' } });
                        }}
                        className="w-full text-left border-2 border-primary/20 rounded-xl p-4 hover:border-primary/50 transition-colors"
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FileText className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-semibold text-sm">Option 1: Add from Website Catalog</h4>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Select products already listed on SolarSavers. Set your price
                                    <span className="text-green-600 font-medium"> (must be ≤ website price)</span>
                                </p>
                                <div className="mt-3 flex items-center gap-2 text-xs">
                                    <Zap className="w-3 h-3 text-yellow-500" />
                                    <span className="text-muted-foreground">Instant approval • No documents needed</span>
                                </div>
                            </div>
                        </div>
                    </button>

                    {/* Option 2: Request New Product */}
                    <button
                        onClick={() => {
                            if (currentStep < steps.length - 1) setCurrentStep(prev => prev + 1);
                            handleMinimize();
                            navigate('/vendor/products', { state: { action: 'suggest_product' } });
                        }}
                        className="w-full text-left border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4 hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Plus className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-semibold text-sm">Option 2: Request New Product Listing</h4>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Have a product not on our website? Submit a request to add it.
                                </p>
                                <div className="mt-3 flex items-center gap-2 text-xs">
                                    <Clock className="w-3 h-3 text-blue-500" />
                                    <span className="text-muted-foreground">Review in 24-48 hours • Documentation required</span>
                                </div>
                            </div>
                        </div>
                    </button>

                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-amber-800 dark:text-amber-300">
                                <strong>Tip:</strong> Start with catalog products to begin selling immediately,
                                then request new products as needed.
                            </p>
                        </div>
                    </div>
                </div>
            ),
            action: {
                label: 'Go to Products Page',
                onClick: (nav) => nav('/vendor/products'),
                icon: ArrowRight
            }
        },
        {
            id: 'orders',
            title: 'Managing Customer Orders',
            shortTitle: 'Orders',
            icon: ShoppingCart,
            targetPage: '/vendor/orders',
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        When customers buy your products, orders appear here.
                    </p>

                    <div className="space-y-3">
                        <h4 className="font-medium text-sm">Order Workflow:</h4>

                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-yellow-700">1</span>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium">New Order Assigned</p>
                                <p className="text-xs text-muted-foreground">You'll receive email notification</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-blue-700">2</span>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium">Process & Ship</p>
                                <p className="text-xs text-muted-foreground">Update status as you fulfill the order</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-green-700">3</span>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium">Complete & Get Paid</p>
                                <p className="text-xs text-muted-foreground">Payment processed after delivery confirmation</p>
                            </div>
                        </div>
                    </div>
                </div>
            ),
            action: {
                label: 'View Orders Page',
                onClick: (nav) => nav('/vendor/orders'),
                icon: Eye
            }
        },
        {
            id: 'profile',
            title: 'Complete Your Profile',
            shortTitle: 'Profile',
            icon: User,
            targetPage: '/vendor/profile',
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        A complete profile builds trust with customers.
                    </p>

                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                        <h4 className="font-medium text-sm">Checklist:</h4>

                        {[
                            { label: 'Business information', done: true },
                            { label: 'Bank details for payments', done: true },
                            { label: 'Documents uploaded', done: true },
                            { label: 'Store description', done: false },
                            { label: 'Contact details', done: false }
                        ].map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${item.done ? 'bg-green-100' : 'bg-muted'
                                    }`}>
                                    {item.done ? (
                                        <CheckCircle className="w-3 h-3 text-green-600" />
                                    ) : (
                                        <div className="w-2 h-2 bg-muted-foreground/30 rounded-full" />
                                    )}
                                </div>
                                <span className={`text-sm ${item.done ? 'text-muted-foreground line-through' : ''}`}>
                                    {item.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            ),
            action: {
                label: 'Edit Profile',
                onClick: (nav) => nav('/vendor/profile'),
                icon: ArrowRight
            }
        },
        {
            id: 'done',
            title: "You're Ready to Start!",
            shortTitle: 'Done',
            icon: Target,
            content: (
                <div className="space-y-4">
                    <div className="text-center py-4">
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-bold text-green-700 dark:text-green-400">Setup Complete!</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            You now know everything to start selling
                        </p>
                    </div>

                    <div className="bg-primary/5 rounded-lg p-4">
                        <h4 className="font-medium text-sm mb-3">Quick Actions:</h4>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => {
                                    handleComplete();
                                    navigate('/vendor/products');
                                }}
                                className="p-3 bg-card border rounded-lg hover:border-primary transition-colors text-center"
                            >
                                <Package className="w-5 h-5 mx-auto mb-1 text-primary" />
                                <span className="text-xs font-medium">Add Products</span>
                            </button>
                            <button
                                onClick={() => {
                                    handleComplete();
                                    navigate('/vendor/orders');
                                }}
                                className="p-3 bg-card border rounded-lg hover:border-primary transition-colors text-center"
                            >
                                <ShoppingCart className="w-5 h-5 mx-auto mb-1 text-primary" />
                                <span className="text-xs font-medium">View Orders</span>
                            </button>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleComplete = () => {
        localStorage.setItem('vendor_onboarding_completed', 'true');
        localStorage.removeItem('vendor_onboarding_step');
        onComplete?.();
    };

    const handleSkip = () => {
        handleComplete();
    };

    const handleMinimize = () => {
        setIsMinimized(true);
    };

    const handleRestore = () => {
        setIsMinimized(false);
    };

    const step = steps[currentStep];
    const StepIcon = step.icon;
    const progress = ((currentStep + 1) / steps.length) * 100;

    // Minimized state - floating button
    if (isMinimized) {
        return (
            <motion.button
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={handleRestore}
                className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            >
                <Sparkles className="w-6 h-6" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full text-xs flex items-center justify-center font-bold">
                    {currentStep + 1}
                </span>
            </motion.button>
        );
    }

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            >
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    className="bg-card rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg overflow-hidden"
                >
                    {/* Progress Bar at Top */}
                    <div className="h-1 bg-muted">
                        <motion.div
                            className="h-full bg-primary"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.3 }}
                        />
                    </div>

                    {/* Header */}
                    <div className="p-4 border-b flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                <StepIcon className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <h2 className="font-bold">{step.title}</h2>
                                <p className="text-xs text-muted-foreground">
                                    Step {currentStep + 1} of {steps.length}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={handleMinimize}
                                className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground"
                                title="Minimize"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={handleSkip}
                                className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground"
                                title="Skip Tour"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Step Indicators */}
                    <div className="px-4 py-2 border-b flex justify-center gap-1">
                        {steps.map((s, idx) => (
                            <button
                                key={s.id}
                                onClick={() => setCurrentStep(idx)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${idx === currentStep
                                    ? 'bg-primary text-white'
                                    : idx < currentStep
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-muted text-muted-foreground'
                                    }`}
                            >
                                {idx < currentStep ? (
                                    <CheckCircle className="w-3 h-3" />
                                ) : (
                                    <span className="w-3 h-3 flex items-center justify-center font-bold">
                                        {idx + 1}
                                    </span>
                                )}
                                <span className="hidden sm:inline">{s.shortTitle}</span>
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="p-4 max-h-[50vh] overflow-y-auto">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentStep}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                            >
                                {step.content}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t bg-muted/30">
                        {/* Action Button */}
                        {step.action && (
                            <Button
                                onClick={() => {
                                    step.action.onClick(navigate);
                                    // Advance to next step before minimizing
                                    if (currentStep < steps.length - 1) {
                                        setCurrentStep(prev => prev + 1);
                                    }
                                    handleMinimize();
                                }}
                                className="w-full mb-3 btn-primary gap-2"
                            >
                                {step.action.label}
                                <step.action.icon className="w-4 h-4" />
                            </Button>
                        )}

                        <div className="flex justify-between">
                            <Button
                                variant="ghost"
                                onClick={handlePrev}
                                disabled={currentStep === 0}
                                className="gap-1"
                                size="sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Back
                            </Button>

                            {currentStep < steps.length - 1 ? (
                                <Button onClick={handleNext} size="sm" className="gap-1">
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            ) : (
                                <Button onClick={handleComplete} size="sm" className="gap-1 bg-green-600 hover:bg-green-700">
                                    Finish
                                    <CheckCircle className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default VendorOnboarding;
